/**
 * API-access connect card for the user Settings page.
 *
 * Surfaces the MCP endpoint so an agent (Claude, Cursor, or a custom client)
 * can connect on behalf of the signed-in user, with a copy affordance and a
 * link to the developer docs. Connecting is a per-user action (the user
 * authorizes with their own holito account), which is why this lives in the
 * user Settings page rather than in workspace settings. Gated on the
 * `api_access` entitlement (Pro and up): locked users see only the upgrade
 * badge, never the endpoint. Kept deliberately minimal: the per-client setup
 * steps live in the docs, not in this surface.
 *
 * @module components/admin/settings/ApiAccessSection
 */
import React, { useCallback, useState } from 'react';
import { IconExternalLink } from '@tabler/icons-react';
import { useEntitlements } from '../../../lib/useEntitlements';
import { mcpApiUrl, HOLITO_API_DOCS_URL } from '../../../lib/worker-urls';
import { captureException } from '../../../lib/telemetry/posthog/frontend';
import { FeatureGateBadge } from '../../ui/FeatureGateBadge';
import { AnimatedLabel } from '../../ui/AnimatedLabel';

const labelClasses =
  'text-xs text-muted-foreground uppercase tracking-wider font-medium block mb-2';
const inputClasses =
  'w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm font-mono focus:outline-none focus:ring-2 focus:ring-volt/50 focus:border-volt';

/** A copy-to-clipboard button that briefly confirms the copy. */
const CopyButton: React.FC<{ value: string; testId: string; label: string }> = ({
  value,
  testId,
  label,
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      captureException(err instanceof Error ? err : new Error(String(err)), 'ApiAccessSection.copy', {
        label,
      });
    }
  }, [value, label]);

  return (
    <button
      type="button"
      data-testid={testId}
      onClick={handleCopy}
      className="shrink-0 px-4 py-2 text-sm rounded-lg bg-surface text-muted-foreground hover:bg-surface-hover hover:text-foreground transition-colors cursor-pointer"
    >
      <AnimatedLabel text={copied ? 'Copied' : 'Copy'} reserveText="Copied" />
    </button>
  );
};

/**
 * API access section of the Settings page. Renders its own heading and gates
 * the connect card on the `api_access` entitlement.
 */
export const ApiAccessSection: React.FC = () => {
  const { can } = useEntitlements();

  return (
    <section>
      <h2 className="text-lg font-semibold text-foreground mb-6">API access</h2>
      {can('api_access') ? (
        <UnlockedCard />
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Connect Claude, Cursor, or your own agent to work with your conversations through the API.
          </p>
          <FeatureGateBadge feature="api_access" />
        </div>
      )}
    </section>
  );
};

/** The unlocked connect card: MCP endpoint, copy button, and docs link. */
const UnlockedCard: React.FC = () => {
  const endpoint = mcpApiUrl();

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Connect Claude, Cursor, or your own agent. Add the endpoint below to your client, then
        authorize with your holito account.
      </p>

      <div>
        <label htmlFor="mcp-endpoint" className={labelClasses}>
          MCP server URL
        </label>
        <div className="flex gap-2">
          <input id="mcp-endpoint" readOnly value={endpoint} className={inputClasses} />
          <CopyButton value={endpoint} testId="api-copy-url" label="mcp-url" />
        </div>
      </div>

      <a
        href={HOLITO_API_DOCS_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 text-sm text-volt hover:underline"
      >
        Read the API docs
        <IconExternalLink className="w-4 h-4" aria-hidden="true" />
      </a>
    </div>
  );
};
